import React, { useMemo } from 'react';

import last from 'lodash/last';

import {
  generateChartLabelsForTimeRange,
  getChartDropdownOptions,
  getPipelineChartLink,
  isZeroOrEmptyChartValue,
} from '../../../utils/chart_utils/chart_utils';

import useCSVChartData from '../helpers/useCSVChartData';
import usePrometheusQuery from '../../../utils/hooks/use_prometheus_query';

import ChartCard from '../ChartCard/ChartCard';
import { ChartProvider } from '../ChartContext';
import MultiAverageDetailPane from '../details/MultiAverageDetailPane';

import { formatCSVDate, formatChartDate } from '../../../utils/date_utils';
import {
  formatSequencedLineChartDatasets,
  getThreeDatasetLineChartDatasets,
} from '../helpers/dataset.helpers';

export const useInferenceLatencyData = ({
  pipelineID,
  startTimestamp,
  endTimestamp,
  step
}) => {
  const prometheusRequestObj = {
    startTimestamp,
    endTimestamp,
    step,
  };

  const inferenceLatency99Response = usePrometheusQuery(
    pipelineID === null
      ? null
      : `histogram_quantile(0.99, sum(rate(inference_latency_histogram_ns_bucket{pipeline_name="${pipelineID}"}[${step}s])) by (le)) * 1000`,
    prometheusRequestObj,
  );
  const inferenceLatency95Response = usePrometheusQuery(
    pipelineID === null
      ? null
      : `histogram_quantile(0.95, sum(rate(inference_latency_histogram_ns_bucket{pipeline_name="${pipelineID}"}[${step}s])) by (le)) * 1000`,
    prometheusRequestObj,
  );
  const inferenceLatency50Response = usePrometheusQuery(
    pipelineID === null
      ? null
      : `histogram_quantile(0.50, sum(rate(inference_latency_histogram_ns_bucket{pipeline_name="${pipelineID}"}[${step}s])) by (le)) * 1000`,
    prometheusRequestObj,
  );

  const chartLabels = useMemo(() => generateChartLabelsForTimeRange(startTimestamp, endTimestamp, step), [startTimestamp, endTimestamp, step]);

  return {
    labels: chartLabels,
    datasets: {
      p99Latency: inferenceLatency99Response?.data,
      p95Latency: inferenceLatency95Response?.data,
      p50Latency: inferenceLatency50Response?.data,
    },
    isLoading: inferenceLatency99Response?.isLoading
      || inferenceLatency95Response?.isLoading
      || inferenceLatency50Response?.isLoading,
    message: inferenceLatency99Response?.error
      || inferenceLatency95Response?.error
      || inferenceLatency50Response?.error
      || null,
  }
}

export const InferenceLatencyChart = ({
  datasets,
  labels,
  labelFormatString,
  isLoading,
  message,
  pipelineID,
  pipelineVersion,
  chartID,
  isEnlarged = false,
}) => {
  const chartTitle = 'Inference Latency';

  const p99LatencyData = datasets.p99Latency
  const p95LatencyData = datasets.p95Latency
  const p50LatencyData = datasets.p50Latency

  const transformedDatasets = useMemo(() => getThreeDatasetLineChartDatasets(p99LatencyData, p95LatencyData, p50LatencyData).map(formatSequencedLineChartDatasets), [p99LatencyData, p95LatencyData, p50LatencyData]);

  const chartLink = useMemo(() => getPipelineChartLink({
    pipelineID, pipelineVersion, chartID,
  }), [chartID, pipelineID, pipelineVersion]);

  const csvDatasets = useMemo(() => {
    if (!transformedDatasets?.length) {
      return [];
    }
    return transformedDatasets?.map(dataset => ({
      ...dataset,
      data: dataset?.data?.map(last)?.map(value => isZeroOrEmptyChartValue(value) ? 0 : value),
    }))
  }, [transformedDatasets])

  const csvData = useCSVChartData({
    chartType: 'multi',
    title: chartTitle,
    data: csvDatasets,
    labels: labels?.map(formatCSVDate),
  })

  const dropdownOptions = getChartDropdownOptions({
    chartLink,
    chartTitle,
    csvData,
    pipelineID,
    pipelineVersion,
    isEnlarged,
  })

  return (
    <ChartProvider
      chartID={chartID}
      chartType="line"
      chartConfiguration="line"
      dropdownOptions={dropdownOptions}
      units="µs"
      datasets={transformedDatasets}
      labels={labels}
      usePaddedMax
    >
      <ChartCard
        chartTitle={chartTitle}
        isLoading={isLoading}
        message={message}
        detailRenderer={MultiAverageDetailPane}
        formatXLabel={formatChartDate(labelFormatString)}
      />
    </ChartProvider>
  )
}

const InferenceLatencyChartWithData = (props) => {
  const {
    pipelineID, startTimestamp, endTimestamp, step
  } = props;

  const {
    datasets,
    labels,
    isLoading,
    message,
  } = useInferenceLatencyData({
    pipelineID, startTimestamp, endTimestamp, step
  })
  return (
    <InferenceLatencyChart
      {...props}
      datasets={datasets}
      labels={labels}
      isLoading={isLoading}
      message={message}
    />
  )
}

export default InferenceLatencyChartWithData;
